import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Spinner, Button, Form, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {createHSDescription, getHSDescriptionByID, updateHSDescriptionById} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {HSDescription} from './core/_models'
import {useNavigate, useParams} from 'react-router-dom'
import './MasterDataHSDescriptionDetailPage.scss'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import SummernoteEditor from '../../../../ui-kit/SummerNote/SummerNote'
import ParentDropdown from './components/ParentDropdown'
import {showSwal} from '../../../../ui-kit'

const MasterDataHSDescriptionDetailPage: FC<{subject: string}> = ({subject}) => {
  const ability = useAbility(AbilityContext)
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const {idHS} = useParams()
  const isDetailMode = Boolean(idHS)
  const isAddMode = !idHS
  const params = new URLSearchParams(window.location.search)
  const HSVersion = params.get('ver')
  const HSCode = params.get('code')
  const [hsDescriptionDetail, setHSDescriptionDetail] = useState<HSDescription>({
    id: idHS ?? HSCode ?? '',
    version: HSVersion ?? '',
    description: '',
    level: 0,
    parent: '',
  })
  const [pendingHsDescription, setPendingHsDescription] = useState<HSDescription>()
  const [oldVersion, setOldVersion] = useState('')
  const [oldId, setOldId] = useState('')

  const validateFormFields = () => {
    const requiredFields = ['version', 'description', 'level', 'id']
    const isValid = requiredFields.every((key) => {
      const val = (hsDescriptionDetail as any)[key]
      return val !== undefined && val !== null && String(val).trim() !== ''
    })

    setIsFormValid(isValid)
  }

  useEffect(() => {
    validateFormFields()
  }, [hsDescriptionDetail])

  const fetchInitData = async () => {
    try {
      setIsLoadingData(true)

      if (isDetailMode) {
        const detailRes = await getHSDescriptionByID(axiosPrivate, {idHS})
        if (detailRes) {
          const resData = detailRes.data.data.original
          setHSDescriptionDetail((prev) => ({
            ...prev,
            description: resData.description,
            description_fr: resData.description_fr,
            parent: resData.parent,
            version: resData.version,
            level: resData.level,
          }))
          setOldVersion(resData.version)
          setOldId(resData.id)

          const pendingData = detailRes.data.data.pending
          if (pendingData != null) setPendingHsDescription(pendingData)
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingData(false)
    }
  }

  useEffect(() => {
    fetchInitData()
  }, [])

  // Update detail HS Category
  const handleDataChange = (e) => {
    const {name, value} = e.target
    const file = (e.target as HTMLInputElement).files?.[0]

    if (file) {
      const reader = new FileReader()

      // Update state if file is read
      reader.onload = (event) => {
        const svgContent = event.target?.result as string
        setHSDescriptionDetail((prev) => ({
          ...prev,
          [name]: svgContent,
        }))
      }

      reader.readAsText(file)
    }

    setHSDescriptionDetail((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = () => {
    const body = {
      id: hsDescriptionDetail.id,
      version: hsDescriptionDetail.version,
      description: hsDescriptionDetail.description,
      level: hsDescriptionDetail.level,
      parent: hsDescriptionDetail.parent,
      description_fr: hsDescriptionDetail.description_fr,
      original_id: isEditMode ? oldId : null,
      old_version: oldVersion,
      old_id: oldId,
    }

    console.log(body)

    showSwal({
      title: `Are you sure you want to ${isEditMode ? 'update' : 'create'} this HS Description?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      setIsLoadingButton(true)

      if (isEditMode) {
        updateHSDescriptionById(axiosPrivate, hsDescriptionDetail.id, body)
          .then((res) => {
            if (res.status === 200) {
              showSwal({
                title: 'Data updated',
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                navigate('/master-data-hs-descriptions')
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', 'Failed to update HS Description', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else {
        createHSDescription(axiosPrivate, body)
          .then((res) => {
            if (res.status === 201) {
              showSwal({
                title: 'Data created',
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                navigate('/master-data-hs-descriptions')
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', 'Failed to create HS Description', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  const handleCancel = () => {
    showSwal({ title: 'Are you sure you want to cancel?', icon: 'question' }).then((result) => {
      if (result.isConfirmed) {
        if (isEditMode) {
          window.location.reload()
        } else {
          navigate('/master-data-hs-description')
        }
      }
    })
  }
  // end of Update detail HS Category

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Master Data - HS',
            path: 'master-data-hs-categories',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isDetailMode ? 'Detail' : 'Add'}
      </PageTitle>
      <div className='MasterDataHSPage'>
        <>
          <Row>
            <Col>
              <Card className='mb-5'>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100'>
                    <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                      <div className='title d-flex align-items-center'>
                        <span className='fa fa-file-alt px-2'></span>
                        <h3 className='text-gray-700'>
                          {isDetailMode
                            ? `Detail ${hsDescriptionDetail.description}`
                            : `Add HS Category`}
                        </h3>
                      </div>
                      {!isEditMode && !isAddMode && ability.can('update', subject) && (
                        <OverlayTrigger
                          placement='top'
                          overlay={
                            pendingHsDescription != null ? (
                              <Tooltip id='edit-disabled-tooltip'>
                                Cannot edit while this HS Description's status is pending for review
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span className='d-inline-block'>
                            <Button
                              className='btn btn-primary d-flex gap-4 align-items-center'
                              onClick={() => setIsEditMode(true)}
                              disabled={pendingHsDescription != null}
                            >
                              <span className='fa fa-pencil'></span>
                              Edit
                            </Button>
                          </span>
                        </OverlayTrigger>
                      )}
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {!isLoadingData ? (
                    <Form className='form-agreement'>
                      <div className='row'>
                        <div className='col-md-6 mb-5'>
                          <Form.Label
                            className={`text-primary me-0 ps-0 ${
                              isEditMode || isAddMode ? 'required' : ''
                            }`}
                            column
                          >
                            HS Code
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <Form.Control
                              placeholder='Enter HS Code'
                              type='text'
                              name='id'
                              value={hsDescriptionDetail.id ?? ''}
                              onChange={handleDataChange}
                            />
                          ) : (
                            <div className='data-value'>{hsDescriptionDetail.id ?? '-'}</div>
                          )}
                        </div>
                        <div className='col-md-6  mb-5'>
                          <Form.Label
                            className={`text-primary me-0 ps-0 ${
                              isEditMode || isAddMode ? 'required' : ''
                            }`}
                            column
                          >
                            HS Version
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <Form.Control
                              placeholder='Enter HS version'
                              type='text'
                              name='version'
                              value={hsDescriptionDetail.version ?? ''}
                              onChange={handleDataChange}
                            />
                          ) : (
                            <div className='data-value'>{hsDescriptionDetail.version ?? '-'}</div>
                          )}
                        </div>
                        <div className='col-md-6  mb-5'>
                          <Form.Label
                            className={`text-primary me-0 ps-0 ${
                              isEditMode || isAddMode ? 'required' : ''
                            }`}
                            column
                          >
                            Level
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <Form.Control
                              placeholder='Enter level'
                              type='text'
                              name='level'
                              value={hsDescriptionDetail.level ?? ''}
                              onChange={handleDataChange}
                            />
                          ) : (
                            <div className='data-value'>{hsDescriptionDetail.level ?? '-'}</div>
                          )}
                        </div>
                        <div className='col-md-6  mb-5'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Parent
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <ParentDropdown
                              parent={hsDescriptionDetail.parent ?? ''}
                              onSelect={(parent) =>
                                setHSDescriptionDetail({
                                  ...hsDescriptionDetail,
                                  parent: parent,
                                })
                              }
                            />
                          ) : (
                            <div className='data-value'>{hsDescriptionDetail.parent ?? '-'}</div>
                          )}
                        </div>
                        <div className='col-md-12 mb-5'>
                          <Form.Label
                            className={`text-primary me-0 ps-0 ${
                              isEditMode || isAddMode ? 'required' : ''
                            }`}
                            column
                          >
                            Description
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <SummernoteEditor
                              value={hsDescriptionDetail.description || ''}
                              options={{
                                toolbar: false,
                              }}
                              onChange={(html) => {
                                setHSDescriptionDetail((prev) => ({
                                  ...prev,
                                  description: html,
                                }))
                                validateFormFields()
                              }}
                            />
                          ) : (
                            <div
                              className='data-value'
                              dangerouslySetInnerHTML={{
                                __html: hsDescriptionDetail.description || '-',
                              }}
                            />
                          )}
                        </div>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Description (Fr)
                          </Form.Label>
                          {isEditMode || isAddMode ? (
                            <SummernoteEditor
                              value={hsDescriptionDetail.description_fr || ''}
                              options={{
                                toolbar: false,
                              }}
                              onChange={(html) => {
                                setHSDescriptionDetail((prev) => ({
                                  ...prev,
                                  description_fr: html,
                                }))
                                validateFormFields()
                              }}
                            />
                          ) : (
                            <div
                              className='data-value'
                              dangerouslySetInnerHTML={{
                                __html: hsDescriptionDetail.description_fr || '-',
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <hr />
                      {isEditMode || isAddMode ? (
                        <div className='row'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={isLoadingButton || !isFormValid}
                            >
                              {isLoadingButton ? (
                                <>
                                  <Spinner animation='border' size='sm' className='me-2' />
                                  Submit
                                </>
                              ) : (
                                'Submit'
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}
                    </Form>
                  ) : (
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{height: '50vh'}}
                    >
                      <Spinner
                        animation='border'
                        role='status'
                        variant='primary'
                        className='me-3'
                      />
                      <div className='loading-text'>
                        Loading
                        <span className='dot'>.</span>
                        <span className='dot'>.</span>
                        <span className='dot'>.</span>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      </div>
    </>
  )
}

export default MasterDataHSDescriptionDetailPage
